import {
    BulkAddKeysForm,
    CreateKeyHistoryRecordForm,
    EntityType,
    KeyData,
    KeyDataForm,
    KeyHistoryRecord,
    KeyHistoryRecordForm,
} from './Model';
import api from "../util/api";
import {replaceElement} from "../util/ArrayUtil";
import {AxiosResponse} from "axios";
import {create} from "zustand";
import {EntityStore, startEntityStore} from "./resourceStoreBuilder";
import {CrudRestResource} from "./RestResource";
import {keyTransactionSubscription} from "./KeyTransaction";
import {keyTagSubscription} from "./KeyTagStore";
import {officeArrayResource} from "./CurrentOfficeStore";
import {KeyData_Read} from "../util/access/Permission";


export interface KeyDataStore extends EntityStore<KeyData[]> {
    createKey: (form: KeyDataForm) => Promise<KeyData>;
    createKeyRange: (form: BulkAddKeysForm) => Promise<KeyData[]>;
    updateKey: (key: KeyData, form: KeyDataForm) => Promise<KeyData>;
    bindNFC: (key: KeyData, transponderId: string) => Promise<KeyData>;
    deleteKey: (row: KeyData) => Promise<void>;

    createHistoryRecord: (key: KeyData, form: CreateKeyHistoryRecordForm) => Promise<void>;
    updateHistoryRecord: (key: KeyData, record: KeyHistoryRecord, form: KeyHistoryRecordForm) => Promise<void>;
    deleteHistoryRecord: (key: KeyData, record: KeyHistoryRecord) => Promise<void>;
}

export const useKeyDataStore = create<KeyDataStore>()(
    officeArrayResource<KeyData, KeyDataStore>(EntityType.KeyData, builder => {
        const keyRest = new CrudRestResource<KeyDataForm, KeyData>(`/offices/${builder.officeId}/key-data`, 'id');
        const recordRest = keyRest.subresource<KeyHistoryRecordForm, KeyHistoryRecord>('/records', 'id')

        return {
            remoteValue: builder.all(KeyData_Read, () => keyRest.getAll()),
            createKey: builder.createAndPatch(keyRest),
            async createKeyRange(form: BulkAddKeysForm) {
                const response: AxiosResponse<KeyData[]> = await api.post(`/offices/${builder.officeId}/key-data/range`, form);
                const rows = response.data;
                builder.patch(prevTableRows => [...rows, ...prevTableRows]);
                return rows;
            },
            updateKey: builder.updateAndPatch(keyRest),
            bindNFC: async (key: KeyData, transponderId: string) => {
                const response: AxiosResponse<KeyData> = await api.patch(`/offices/${builder.officeId}/key-data/${key.id}/transponderId`, transponderId);
                const updatedEntity = response.data;
                builder.patch(prevEntities => replaceElement(prevEntities, (oldEntity) => (oldEntity === key), updatedEntity));
                return updatedEntity;
            },
            deleteKey: builder.deleteAndPatch(keyRest),
            async createHistoryRecord(key: KeyData, form: CreateKeyHistoryRecordForm) {
                const newHistoryRecord = await recordRest(key).create(form);
                let newKey = addRecord(key, newHistoryRecord);

                if (form.finishPreviousRecords) {
                    for (const previousActiveRecord of key.history.filter(record => !record.to)) {
                        const updatedRecord = await recordRest(key).update(previousActiveRecord, {
                            ...previousActiveRecord,
                            to: form.from
                        });
                        newKey = updateRecord(newKey, previousActiveRecord, updatedRecord);
                    }
                }
                builder.patch(replaceKeyData(key, newKey));
            },
            async updateHistoryRecord(key: KeyData, record: KeyHistoryRecord, form: KeyHistoryRecordForm) {
                const newHistoryRecord = await recordRest(key).update(record, form);
                const newKey = updateRecord(key, record, newHistoryRecord);
                builder.patch(replaceKeyData(key, newKey));
            },
            async deleteHistoryRecord(key: KeyData, record: KeyHistoryRecord) {
                await recordRest(key).delete(record);
                const newKey = deleteRecord(key, record);
                builder.patch(replaceKeyData(key, newKey));
            },
        };
    })
);

export function activeRecord(key: KeyData): KeyHistoryRecord | undefined {
    for (let i = key.history.length - 1; i >= 0; i--) {
        const record = key.history[i];
        if (!record.to) return record;
    }
    return undefined;
}

startEntityStore(useKeyDataStore, keyTransactionSubscription, keyTagSubscription);

function addRecord(keyData: KeyData, newRecord: KeyHistoryRecord): KeyData {
    return {...keyData, history: [...keyData.history, newRecord]};
}

function deleteRecord(keyData: KeyData, oldRecord: KeyHistoryRecord): KeyData {
    return {...keyData, history: keyData.history.filter(record => record.id !== oldRecord.id)};
}

function updateRecord(keyData: KeyData, oldRecord: KeyHistoryRecord, newRecord: KeyHistoryRecord): KeyData {
    return {...keyData, history: replaceElement(keyData.history, record => record.id === oldRecord.id, newRecord)};
}

function replaceKeyData(oldKey: KeyData, newKey: KeyData): (prevKeys: KeyData[]) => KeyData[] {
    return prevKeys => replaceElement(prevKeys, prevKey => prevKey === oldKey, newKey);
}
